import React from "react";
import "./SolutionDetailsFeatures.css";

const GENERIC_FEATURE_TITLE = /^key features(\s*&\s*benefits)?[:\s]*$/i;

const normalizeFeatureRow = (f) => {
  let title = String(f?.title || "").trim();
  let desc = String(
    f?.description || f?.text || f?.body || f?.desc || ""
  ).trim();

  // Seeded ceiling/interior rows often use a generic title with "Label: body" in description.
  if (GENERIC_FEATURE_TITLE.test(title) && desc) {
    const colonIndex = desc.indexOf(":");
    if (colonIndex > 0) {
      title = desc.slice(0, colonIndex).trim();
      desc = desc.slice(colonIndex + 1).trim();
    } else {
      title = "";
    }
  }

  return { title, desc };
};

const toFeatureRows = (list) =>
  Array.isArray(list)
    ? [...list]
        .sort((a, b) => {
          const orderA =
            typeof a?.displayOrder === "number" ? a.displayOrder : Number.MAX_SAFE_INTEGER;
          const orderB =
            typeof b?.displayOrder === "number" ? b.displayOrder : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        })
        .map(normalizeFeatureRow)
        .filter((f) => f.title || f.desc)
    : [];

const parseDetailsTextToFeatures = (detailsText) => {
  const text = String(detailsText || "").trim();
  if (!text) return [];

  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !GENERIC_FEATURE_TITLE.test(line))
    .map((line) => {
      const colonIndex = line.indexOf(":");
      if (colonIndex <= 0) {
        return { title: line, desc: "" };
      }
      return {
        title: line.slice(0, colonIndex).trim(),
        desc: line.slice(colonIndex + 1).trim(),
      };
    })
    .filter((f) => f.title || f.desc);
};

const pickFirstFeatureList = (...candidates) => {
  for (const list of candidates) {
    if (Array.isArray(list) && list.length) return list;
  }
  return [];
};

const SolutionDetailsFeatures = ({ solution, activeTabIndex = 0 }) => {
  const tabs = Array.isArray(solution?.descriptionTabs)
    ? [...solution.descriptionTabs]
        .filter((tab) => typeof tab === "object" && tab != null)
        .sort(
          (a, b) =>
            (typeof a?.displayOrder === "number" ? a.displayOrder : Number.MAX_SAFE_INTEGER) -
            (typeof b?.displayOrder === "number" ? b.displayOrder : Number.MAX_SAFE_INTEGER)
        )
    : [];
  const safeTabIndex =
    Number.isFinite(activeTabIndex) && activeTabIndex >= 0
      ? Math.min(activeTabIndex, Math.max(0, tabs.length - 1))
      : 0;
  const activeTab = tabs[safeTabIndex] || null;

  const tabStructuredFeatures = toFeatureRows(
    pickFirstFeatureList(
      activeTab?.features,
      activeTab?.keyFeatures,
      activeTab?.benefits,
      Array.isArray(solution?.featuresByTab) ? solution.featuresByTab[safeTabIndex] : null
    )
  );
  const tabDetailsFeatures = parseDetailsTextToFeatures(activeTab?.details);
  const tabFeatures = tabStructuredFeatures.length ? tabStructuredFeatures : tabDetailsFeatures;
  const defaultFeatures = toFeatureRows(solution?.features);
  const data = tabFeatures.length ? tabFeatures : defaultFeatures;

  if (!data.length) return null;

  const featuresTag = solution?.featuresTag || "FEATURES AND BENEFITS";
  const featuresTitle = solution?.featuresTitle || "Key Features";
  const featuresTitleAccent = solution?.featuresTitleAccent || "and Benefits";

  return (
    <section className="featuresSection">
      <div className="featuresContainer">
        <span className="featuresTag">{featuresTag}</span>

        <h2 className="featuresTitle">
          {featuresTitle} <span>{featuresTitleAccent}</span>
        </h2>

        <div className="featuresGrid">
          {data.map((item, index) => (
            <div key={index} className="featureItem">
              {item.title ? <h3>{item.title}</h3> : null}
              {item.desc ? <p>{item.desc}</p> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionDetailsFeatures;
